import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Slider,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { useSubscriptionParameterFresh } from '@/hooks/useSubscriptionParameterFresh'
import { tagsApi, type Tag } from '@/api/tagsApi'
import { dictionariesApi } from '@/api/dictionaries'
import {
  dictionaryFillApi,
  type DictionaryFillApiError,
  type DictionaryFillStudentPreview,
} from '@/api/dictionaryFill'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { ROUTES } from '@/utils/constants'
import type { Dictionary } from '@/types'
import '@/styles/dashboard.css'

const PREVIEW_DEBOUNCE_MS = 1500
const WORD_LIMIT_MAX = 1000

/**
 * Создание прогонов «Пополнить словарь» из урока: ученики берутся из
 * участников урока (для группового — по прогону на каждого), количество слов —
 * ползунок 0–1000 с галочкой «Все слова». Без премиума форма закрыта
 * полупрозрачным оверлеем с CTA на подписку.
 */
export function LessonDictionaryFillCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams<{ lessonId: string }>()
  const lessonId = Number(params.lessonId)

  const hasPremium = useSubscriptionParameterFresh('dictionary_fill_enabled')

  const [tags, setTags] = useState<Tag[]>([])
  const [dictionaries, setDictionaries] = useState<Dictionary[]>([])
  const [isRefsLoading, setIsRefsLoading] = useState(true)

  const [selectedTags, setSelectedTags] = useState<Tag[]>([])
  const [dictionaryId, setDictionaryId] = useState<number | ''>('')
  const [wordType, setWordType] = useState<'' | 'word' | 'phrase'>('')
  const [allWords, setAllWords] = useState(false)
  const [sliderLimit, setSliderLimit] = useState(100)

  const [preview, setPreview] = useState<DictionaryFillStudentPreview[] | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [subscriptionError, setSubscriptionError] = useState(false)

  const previewTimerRef = useRef<number | null>(null)
  const hasInitialPreviewRef = useRef(false)
  /** Применяем только ответ последнего запроса превью (ранний не должен перетирать поздний). */
  const previewSeqRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsRefsLoading(true)
      try {
        const [tagsResp, dictsResp] = await Promise.all([
          tagsApi.getList({ per_page: 100 }),
          dictionariesApi.getDictionaries({ per_page: 100 }),
        ])
        if (cancelled) return
        setTags(tagsResp.data)
        setDictionaries(dictsResp.data)
      } catch (err) {
        if (!cancelled) setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
      } finally {
        if (!cancelled) setIsRefsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [t])

  const filters = useMemo(
    () => ({
      tag_ids: selectedTags.map((tag) => tag.id),
      dictionary_id: dictionaryId === '' ? undefined : dictionaryId,
      word_type: wordType === '' ? undefined : wordType,
    }),
    [selectedTags, dictionaryId, wordType],
  )

  const runPreview = useCallback(async () => {
    if (!Number.isFinite(lessonId)) return
    const seq = ++previewSeqRef.current
    setIsPreviewLoading(true)
    try {
      const rows = await dictionaryFillApi.previewForLesson(lessonId, filters)
      if (seq !== previewSeqRef.current) return
      setPreview(rows)
      setSubscriptionError(false)
      setError(null)
    } catch (err) {
      if (seq !== previewSeqRef.current) return
      const rich = err as DictionaryFillApiError
      if (rich.code === 'subscription_parameter_denied' || rich.code === 'subscription_required') {
        setSubscriptionError(true)
      } else {
        // Последнее удачное превью не обнуляем: сетевой сбой не должен
        // блокировать кнопку старта до смены фильтров.
        setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
      }
    } finally {
      if (seq === previewSeqRef.current) setIsPreviewLoading(false)
    }
  }, [lessonId, filters, t])

  useEffect(() => {
    if (!hasPremium || !Number.isFinite(lessonId)) return
    if (!hasInitialPreviewRef.current) {
      hasInitialPreviewRef.current = true
      void runPreview()
      return
    }
    if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    previewTimerRef.current = window.setTimeout(() => {
      void runPreview()
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    }
  }, [hasPremium, lessonId, filters, runPreview])

  const wordLimit = allWords ? null : sliderLimit

  const totalAvailable = useMemo(
    () => (preview ?? []).reduce((sum, row) => sum + row.words_available, 0),
    [preview],
  )

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      const runs = await dictionaryFillApi.createForLesson(lessonId, {
        ...filters,
        word_limit: wordLimit,
      })
      if (runs.length > 0) {
        navigate(ROUTES.DICTIONARY_FILL_RUN(runs[0].id))
      } else {
        // Бэкенд отвечает 422, если прогоны не созданы; ветка — страховка от смены контракта.
        setError(t('dictionaryFill.preview.nothingToFill'))
      }
    } catch (err) {
      const rich = err as DictionaryFillApiError
      if (rich.code === 'subscription_parameter_denied' || rich.code === 'subscription_required') {
        setSubscriptionError(true)
      }
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setIsSubmitting(false)
    }
  }

  const premiumGate = !hasPremium || subscriptionError
  const startDisabled =
    premiumGate || isSubmitting || (!allWords && sliderLimit === 0) || totalAvailable === 0

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" color="inherit">
            {t('dashboard.home')}
          </Link>
          <Link
            component={RouterLink}
            to={ROUTES.LESSON_DETAILS(lessonId)}
            underline="hover"
            color="inherit"
          >
            {t('dictionaryFill.form.lessonCrumb', { id: lessonId })}
          </Link>
          <Typography color="text.primary">{t('dictionaryFill.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('dictionaryFill.title')}
          </Typography>
          <PageHelpPopover pageKey="dictionary-fill-start" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Оверлей недоступности закрывает форму целиком (полупрозрачный блок). */}
        <Box sx={{ position: 'relative' }}>
          {premiumGate && (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.background.default, 0.7),
                backdropFilter: 'blur(3px)',
                textAlign: 'center',
                p: 3,
              })}
            >
              <Typography variant="h6" fontWeight={700}>
                {t('dictionaryFill.premiumOnly')}
              </Typography>
              <Button
                component={RouterLink}
                to={ROUTES.PROFILE_SUBSCRIPTION}
                variant="contained"
                size="small"
              >
                {t('subscription.goToSubscription')}
              </Button>
            </Box>
          )}

          {isRefsLoading ? (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 360px' } }}>
              <Skeleton variant="rounded" height={420} />
              <Skeleton variant="rounded" height={220} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', md: '1fr 360px' },
                filter: premiumGate ? 'grayscale(0.4)' : 'none',
                pointerEvents: premiumGate ? 'none' : 'auto',
              }}
            >
              <Card sx={{ p: 0 }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h6" gutterBottom>
                    {t('dictionaryFill.form.sectionTitle')}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={tags}
                      value={selectedTags}
                      onChange={(_e, value) => setSelectedTags(value)}
                      getOptionLabel={(tag) => tag.name}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      renderInput={(params) => (
                        <TextField {...params} label={t('dictionaryFill.form.tags')} />
                      )}
                    />

                    <FormControl size="small" fullWidth>
                      <InputLabel id="dfill-dictionary-label">
                        {t('dictionaryFill.form.dictionary')}
                      </InputLabel>
                      <Select
                        labelId="dfill-dictionary-label"
                        label={t('dictionaryFill.form.dictionary')}
                        value={dictionaryId}
                        onChange={(e) => setDictionaryId(e.target.value as number | '')}
                      >
                        <MenuItem value="">{t('dictionaryFill.form.allDictionaries')}</MenuItem>
                        {dictionaries.map((d) => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" fullWidth>
                      <InputLabel id="dfill-word-type-label">
                        {t('dictionaryFill.form.wordType')}
                      </InputLabel>
                      <Select
                        labelId="dfill-word-type-label"
                        label={t('dictionaryFill.form.wordType')}
                        value={wordType}
                        onChange={(e) => setWordType(e.target.value as '' | 'word' | 'phrase')}
                      >
                        <MenuItem value="">{t('dictionaryFill.form.anyWordType')}</MenuItem>
                        <MenuItem value="word">{t('dictionaryFill.form.wordTypeWord')}</MenuItem>
                        <MenuItem value="phrase">{t('dictionaryFill.form.wordTypePhrase')}</MenuItem>
                      </Select>
                    </FormControl>

                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {t('dictionaryFill.form.wordLimit')}
                        {!allWords && (
                          <Typography component="span" variant="body2" fontWeight={700} sx={{ ml: 1 }}>
                            {sliderLimit}
                          </Typography>
                        )}
                      </Typography>
                      <Box sx={{ px: 1 }}>
                        <Slider
                          min={0}
                          max={WORD_LIMIT_MAX}
                          step={10}
                          value={sliderLimit}
                          disabled={allWords}
                          onChange={(_e, value) => setSliderLimit(value as number)}
                          valueLabelDisplay="auto"
                          marks={[
                            { value: 0, label: '0' },
                            { value: 500, label: '500' },
                            { value: 1000, label: '1000' },
                          ]}
                          sx={{
                            // Крайние метки прижимаются внутрь, чтобы «0» и «1000» не обрезались краем контейнера.
                            '& .MuiSlider-markLabel[data-index="0"]': { transform: 'none' },
                            '& .MuiSlider-markLabel[data-index="2"]': { transform: 'translateX(-100%)' },
                          }}
                        />
                      </Box>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allWords}
                            onChange={(e) => setAllWords(e.target.checked)}
                          />
                        }
                        label={t('dictionaryFill.form.allWords')}
                      />
                    </Box>

                    <Box>
                      <Button
                        variant="contained"
                        disabled={startDisabled}
                        onClick={() => void handleSubmit()}
                        startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
                      >
                        {t('dictionaryFill.form.start')}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Paper
                variant="outlined"
                sx={{ p: 2, alignSelf: 'start', position: { md: 'sticky' }, top: { md: 16 } }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {t('dictionaryFill.preview.title')}
                </Typography>
                {isPreviewLoading ? (
                  <LinearProgress sx={{ height: 4, mb: 2 }} />
                ) : (
                  <Box sx={{ height: 4, mb: 2 }} />
                )}
                {preview === null ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('dictionaryFill.preview.empty')}
                  </Typography>
                ) : preview.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('dictionaryFill.preview.noStudents')}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {preview.map((row) => (
                      <Paper key={row.student_id} variant="outlined" sx={{ p: 1.25 }}>
                        <Typography variant="body2" fontWeight={700}>
                          {row.student_name || `#${row.student_id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('dictionaryFill.preview.studentCounts', {
                            available: row.words_available,
                            skipped: row.words_skipped_existing_cards,
                          })}
                        </Typography>
                      </Paper>
                    ))}
                    {totalAvailable === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('dictionaryFill.preview.nothingToFill')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </Box>
      </Box>
    </DashboardLayout>
  )
}
